import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, UserX, UserCheck, Users, Pencil, KeyRound, Phone, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import { usersApi, presenceApi } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import Input, { Select } from '../../components/ui/Input'
import Badge from '../../components/ui/Badge'
import LoadingState from '../../components/ui/LoadingState'
import { useConfirm } from '../../components/ui/ConfirmDialog'
import { fmtDateTime, fmtDate } from '../../utils/date'
import EmptyState from '../../components/ui/EmptyState'

const ROLE_OPTIONS = [
  { value: 'sales_rep', label: 'Sales Rep' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
]

function RoleBadge({ role }: { role: string }) {
  const map: Record<string, 'purple' | 'info' | 'warning' | 'success'> = { admin: 'purple', manager: 'info', marketing: 'success', sales_rep: 'warning' }
  return <Badge variant={map[role] || 'default'}>{role.replace('_', ' ')}</Badge>
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 120) return 'Just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

export default function UsersPage() {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuthStore()
  const isAdmin = currentUser?.role === 'admin'
  const [showInactive, setShowInactive] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<any | null>(null)
  const [resetPasswordUser, setResetPasswordUser] = useState<any | null>(null)
  const [provisionModalOpen, setProvisionModalOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['users', showInactive],
    queryFn: () => usersApi.list(showInactive),
  })

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => usersApi.deactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  const reactivateMutation = useMutation({
    mutationFn: (id: number) => usersApi.update(id, { is_active: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  const users = data?.users || []

  // Fetch live presence for "Last Active" column (admin/manager only)
  const isManager = currentUser?.role === 'admin' || currentUser?.role === 'manager'
  const { data: presenceData } = useQuery({
    queryKey: ['presence', 'live'],
    queryFn: async () => { const res = await presenceApi.getLive(); return res.data },
    refetchInterval: 60_000,
    enabled: isManager,
  })
  const presenceMap = new Map<number, { status: string; last_seen_at: string }>(
    (presenceData?.online || []).map((u: any) => [u.user_id, { status: u.status, last_seen_at: u.last_seen_at }]),
  )

  const [confirmNode, confirm] = useConfirm()

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer min-h-[36px]">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded" />
            Show inactive
          </label>
          {isAdmin && (
            <Button variant="secondary" size="sm" onClick={() => setProvisionModalOpen(true)}>
              <Phone size={14} /> Provision VoIP
            </Button>
          )}
          <Button size="sm" onClick={() => setModalOpen(true)}>
            <Plus size={14} /> Add User
          </Button>
        </div>
      </div>

      {confirmNode}

      {isLoading ? (
        <LoadingState message="Loading users..." />
      ) : users.length === 0 ? (
        <EmptyState icon={Users} title="No users" description="Create your first team member" action={<Button onClick={() => setModalOpen(true)}><Plus size={16} /> Add User</Button>} />
      ) : (
        <>
        {/* Mobile card view */}
        <div className="md:hidden space-y-2">
          {users.map((u) => (
            <div key={u.id} onClick={() => setEditingUser(u)} className={`bg-white rounded-xl border border-gray-200 p-4 active:bg-gray-50 ${u.is_active ? '' : 'opacity-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${u.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <p className="text-sm font-semibold text-gray-900">{u.full_name || u.username}</p>
                    <RoleBadge role={u.role} />
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 ml-4">{u.email || u.username}</p>
                </div>
                <div className="text-xs text-gray-400">
                  {(() => { const p = presenceMap.get(u.id); return p ? timeAgo(p.last_seen_at) : 'Offline' })()}
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Desktop table */}
        <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Last Active</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Last Login</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => (
                <tr key={u.id} className={`${u.is_active ? '' : 'opacity-50'}`}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{u.full_name || u.username}</p>
                    <p className="text-xs text-gray-500">{u.email || u.username}</p>
                  </td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-4 py-3">
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700"><span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500"><span className="w-1.5 h-1.5 bg-gray-400 rounded-full" /> Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {(() => {
                      const p = presenceMap.get(u.id)
                      if (!p) return <span className="text-gray-400">Offline</span>
                      return (
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${p.status === 'active' ? 'bg-green-500' : 'bg-yellow-400'}`} />
                          <span className={p.status === 'active' ? 'text-green-700' : 'text-yellow-700'}>
                            {timeAgo(p.last_seen_at)}
                          </span>
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {u.last_login_at ? fmtDateTime(u.last_login_at) : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {fmtDate(u.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditingUser(u)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
                        title="Edit user"
                      >
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        onClick={() => setResetPasswordUser(u)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
                        title="Reset password"
                      >
                        <KeyRound size={13} /> Reset Password
                      </button>
                      {u.is_active ? (
                        <button
                          onClick={async () => { if (await confirm({ message: `Are you sure you want to deactivate ${u.username}?`, title: 'Deactivate User', confirmLabel: 'Deactivate', confirmVariant: 'danger' })) deactivateMutation.mutate(u.id) }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <UserX size={13} /> Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => reactivateMutation.mutate(u.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-green-600 hover:bg-green-50 rounded-lg"
                        >
                          <UserCheck size={13} /> Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <CreateUserModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} isAdmin={isAdmin} />
      <ResetPasswordModal user={resetPasswordUser} onClose={() => setResetPasswordUser(null)} />
      {isAdmin && <ProvisionModal open={provisionModalOpen} onClose={() => setProvisionModalOpen(false)} />}
    </div>
  )
}

function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({ username: '', password: '', full_name: '', email: '', role: 'sales_rep' })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => usersApi.create(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setForm({ username: '', password: '', full_name: '', email: '', role: 'sales_rep' })
      setError('')
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  return (
    <Modal open={open} onClose={onClose} title="Create User">
      <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form) }} className="space-y-4">
        {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
        <Input label="Username *" value={form.username} onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))} required minLength={3} />
        <Input label="Password *" type="password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} required minLength={8} />
        <Input label="Full Name" value={form.full_name} onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))} />
        <Input label="Email" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
        <Select label="Role" options={ROLE_OPTIONS} value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending}>Create User</Button>
        </div>
      </form>
    </Modal>
  )
}

function EditUserModal({ user, onClose, isAdmin }: { user: any | null; onClose: () => void; isAdmin: boolean }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({ username: '', full_name: '', email: '', role: '', max_lead_capacity: '50', plivo_endpoint_username: '', plivo_endpoint_password: '', show_full_contact_info: false, allow_data_transfers: false, phone_number: '', otp_enabled: false })
  const [error, setError] = useState('')

  // Sync form state when user changes
  const prevId = useState<number | null>(null)
  if (user && user.id !== prevId[0]) {
    prevId[1](user.id)
    setForm({ username: user.username || '', full_name: user.full_name || '', email: user.email || '', role: user.role || 'sales_rep', max_lead_capacity: String(user.max_lead_capacity ?? 50), plivo_endpoint_username: user.plivo_endpoint_username || '', plivo_endpoint_password: '', show_full_contact_info: user.show_full_contact_info || false, allow_data_transfers: user.allow_data_transfers || false, phone_number: user.phone_number || '', otp_enabled: user.otp_enabled || false })
    setError('')
  }
  if (!user && prevId[0] !== null) {
    prevId[1](null)
  }

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => usersApi.update(user!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  if (!user) return null

  return (
    <Modal open={!!user} onClose={onClose} title={`Edit User — ${user.username}`}>
      <form onSubmit={(e) => {
        e.preventDefault()
        const payload: Record<string, unknown> = { ...form }
        if (form.max_lead_capacity) payload.max_lead_capacity = parseInt(form.max_lead_capacity, 10)
        if (!payload.plivo_endpoint_password) delete payload.plivo_endpoint_password
        mutation.mutate(payload)
      }} className="space-y-4">
        {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

        <Input
          label="Username"
          value={form.username}
          onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))}
          required
          minLength={3}
        />

        <Input
          label="Full Name"
          value={form.full_name}
          onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))}
        />

        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
        />

        <Select
          label="Role"
          options={ROLE_OPTIONS}
          value={form.role}
          onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
        />

        <Input
          label="Max Lead Capacity"
          type="number"
          value={form.max_lead_capacity}
          onChange={(e) => setForm(f => ({ ...f, max_lead_capacity: e.target.value }))}
          placeholder="50"
        />
        <p className="text-[11px] text-gray-400 -mt-2 ml-1">Maximum leads this user can be assigned. Used for load balancing and workload display.</p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
          <p className="font-semibold mb-1">Role Permissions</p>
          {form.role === 'admin' && <p>Full access — user management, settings, all features.</p>}
          {form.role === 'manager' && <p>Dashboard, reports, leads, CRM, AI tools, team workload. No user management or settings.</p>}
          {form.role === 'marketing' && <p>AI tools (blog, social, campaign, strategy), Meta Ads, run history. No leads or CRM.</p>}
          {form.role === 'sales_rep' && <p>Leads, companies, tasks, email, VoIP calls. No dashboard, reports, or AI tools.</p>}
        </div>

        {/* Phone & 2FA */}
        <div className="border-t border-gray-100 pt-4 mt-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Phone & Two-Factor Authentication</p>
          <div className="space-y-3">
            <Input
              label="Phone Number"
              value={form.phone_number}
              onChange={(e) => setForm(f => ({ ...f, phone_number: e.target.value }))}
              placeholder="+91XXXXXXXXXX"
            />
            {isAdmin && (
              <>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.otp_enabled}
                    onChange={(e) => setForm(f => ({ ...f, otp_enabled: e.target.checked }))}
                    disabled={!form.phone_number}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                  />
                  Enable OTP two-factor authentication
                </label>
                {form.otp_enabled && !form.phone_number && (
                  <p className="text-xs text-red-500 ml-6">Phone number required to enable 2FA</p>
                )}
                <p className="text-[11px] text-gray-400 ml-6">
                  When enabled, this user must enter a one-time code sent to their phone after entering their password.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 mt-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">VoIP Endpoint (Plivo)</p>
          <div className="space-y-3">
            <Input
              label="Endpoint Username"
              placeholder="e.g. opsnest_agent_1"
              value={form.plivo_endpoint_username}
              onChange={(e) => setForm(f => ({ ...f, plivo_endpoint_username: e.target.value }))}
            />
            <Input
              label="Endpoint Password"
              type="password"
              placeholder={user.has_plivo_endpoint ? 'Leave blank to keep current' : 'Set endpoint password'}
              value={form.plivo_endpoint_password}
              onChange={(e) => setForm(f => ({ ...f, plivo_endpoint_password: e.target.value }))}
            />
            {isAdmin && <ProvisionButton userId={user.id} hasEndpoint={user.has_plivo_endpoint} onProvisioned={(username) => setForm(f => ({ ...f, plivo_endpoint_username: username }))} />}
          </div>
        </div>

        {/* Data Privacy */}
        {isAdmin && (
          <div className="border-t border-gray-100 pt-4 mt-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Data Privacy</p>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.show_full_contact_info}
                onChange={(e) => setForm(f => ({ ...f, show_full_contact_info: e.target.checked }))}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show full contact info (override system masking)
            </label>
            <p className="text-[11px] text-gray-400 mt-1 ml-6">
              When system-wide masking is enabled, this user will still see full phone numbers, emails, and addresses.
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-3">
              <input
                type="checkbox"
                checked={form.allow_data_transfers}
                onChange={(e) => setForm(f => ({ ...f, allow_data_transfers: e.target.checked }))}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Allow data import/export
            </label>
            <p className="text-[11px] text-gray-400 mt-1 ml-6">
              Grants this user permission to import and export CSV files. By default, only admins can export and admins/managers can import.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending}>Save Changes</Button>
        </div>
      </form>
    </Modal>
  )
}

function ResetPasswordModal({ user, onClose }: { user: any | null; onClose: () => void }) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const mutation = useMutation({
    mutationFn: (newPassword: string) => usersApi.resetPassword(user!.id, { new_password: newPassword }),
    onSuccess: () => {
      setSuccess(true)
      setPassword('')
      setConfirmPassword('')
      setTimeout(() => { setSuccess(false); onClose() }, 2000)
    },
    onError: (err: Error) => setError(err.message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    mutation.mutate(password)
  }

  if (!user) return null

  return (
    <Modal open={!!user} onClose={onClose} title="Reset Password">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
        {success && <div className="p-3 bg-green-50 text-green-700 text-sm rounded-lg">Password reset successfully!</div>}

        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">Resetting password for</p>
          <p className="text-sm font-medium text-gray-900">{user.full_name || user.username}</p>
          <p className="text-xs text-gray-500">{user.username}</p>
        </div>

        <Input
          label="New Password *"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          placeholder="Minimum 8 characters"
        />

        <Input
          label="Confirm Password *"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={8}
          placeholder="Re-enter password"
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending}>Reset Password</Button>
        </div>
      </form>
    </Modal>
  )
}


interface ProvisionResult {
  userId: number
  username: string
  success: boolean
  endpointUsername?: string
  error?: string
}

function ProvisionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [isProvisioning, setIsProvisioning] = useState(false)
  const [results, setResults] = useState<ProvisionResult[]>([])

  const { data, isLoading } = useQuery({
    queryKey: ['users', false],
    queryFn: () => usersApi.list(false),
    enabled: open,
  })

  const allUsers = data?.users || []
  const unprovisionedUsers = allUsers.filter((u: any) => !u.has_plivo_endpoint && u.is_active)
  const provisionedUsers = allUsers.filter((u: any) => u.has_plivo_endpoint)

  const toggleUser = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    if (selected.size === unprovisionedUsers.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(unprovisionedUsers.map((u: any) => u.id as number)))
    }
  }

  const handleProvision = async () => {
    if (selected.size === 0) return
    setIsProvisioning(true)
    setResults([])
    const provisionResults: ProvisionResult[] = []

    for (const userId of selected) {
      const user = allUsers.find((u: any) => u.id === userId)
      const displayName = (user as any)?.full_name || (user as any)?.username || `User ${userId}`
      try {
        const resp = await usersApi.provisionPlivoEndpoint(userId)
        provisionResults.push({
          userId,
          username: displayName,
          success: true,
          endpointUsername: resp.data?.username,
        })
      } catch (err: any) {
        provisionResults.push({
          userId,
          username: displayName,
          success: false,
          error: err?.message || 'Provisioning failed',
        })
      }
      setResults([...provisionResults])
    }

    setIsProvisioning(false)
    queryClient.invalidateQueries({ queryKey: ['users'] })
  }

  const handleClose = () => {
    if (!isProvisioning) {
      setSelected(new Set())
      setResults([])
      onClose()
    }
  }

  const successCount = results.filter((r) => r.success).length
  const failureCount = results.filter((r) => !r.success).length
  const isDone = results.length > 0 && !isProvisioning

  return (
    <Modal open={open} onClose={handleClose} title="Provision VoIP Endpoints" size="lg">
      <div className="space-y-4">
        {/* Warning banner */}
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            This will create Plivo SIP endpoints for the selected users. Each endpoint incurs a monthly cost.
          </p>
        </div>

        {/* Results summary */}
        {isDone && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm font-medium text-gray-900 mb-2">Provisioning complete</p>
            <div className="flex items-center gap-4">
              {successCount > 0 && (
                <span className="inline-flex items-center gap-1.5 text-sm text-green-700">
                  <CheckCircle2 size={15} /> {successCount} provisioned
                </span>
              )}
              {failureCount > 0 && (
                <span className="inline-flex items-center gap-1.5 text-sm text-red-700">
                  <XCircle size={15} /> {failureCount} failed
                </span>
              )}
            </div>
            {results.filter((r) => !r.success).length > 0 && (
              <div className="mt-2 space-y-1">
                {results.filter((r) => !r.success).map((r) => (
                  <p key={r.userId} className="text-xs text-red-600">{r.username}: {r.error}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-gray-400">Loading users...</div>
        ) : (
          <>
            {/* Select all toggle */}
            {unprovisionedUsers.length > 0 && !isDone && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.size === unprovisionedUsers.length && unprovisionedUsers.length > 0}
                    onChange={selectAll}
                    className="rounded"
                    disabled={isProvisioning}
                  />
                  Select all ({unprovisionedUsers.length} user{unprovisionedUsers.length !== 1 ? 's' : ''} without endpoint)
                </label>
              </div>
            )}

            {/* User list */}
            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {unprovisionedUsers.length === 0 && provisionedUsers.length === 0 && (
                <div className="p-4 text-center text-sm text-gray-500">No users found</div>
              )}
              {unprovisionedUsers.map((u: any) => {
                const result = results.find((r) => r.userId === u.id)
                return (
                  <label
                    key={u.id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 ${isProvisioning ? 'pointer-events-none' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleUser(u.id)}
                      className="rounded"
                      disabled={isProvisioning}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.full_name || u.username}</p>
                      <p className="text-xs text-gray-500">{u.email || u.username}</p>
                    </div>
                    <RoleBadge role={u.role} />
                    {result && (
                      result.success
                        ? <CheckCircle2 size={16} className="text-green-500 flex-shrink-0" />
                        : <XCircle size={16} className="text-red-500 flex-shrink-0" />
                    )}
                  </label>
                )
              })}
              {provisionedUsers.map((u: any) => (
                <div key={u.id} className="flex items-center gap-3 px-4 py-3 opacity-50">
                  <input type="checkbox" checked={false} disabled className="rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-500 truncate">{u.full_name || u.username}</p>
                    <p className="text-xs text-gray-400">{u.email || u.username}</p>
                  </div>
                  <RoleBadge role={u.role} />
                  <Badge variant="success">Configured</Badge>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={isProvisioning}>
            {isDone ? 'Close' : 'Cancel'}
          </Button>
          {!isDone && (
            <Button
              onClick={handleProvision}
              loading={isProvisioning}
              disabled={selected.size === 0 || isProvisioning}
            >
              <Phone size={16} />
              Provision {selected.size > 0 ? `${selected.size} User${selected.size !== 1 ? 's' : ''}` : 'Selected'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}


function ProvisionButton({ userId, hasEndpoint, onProvisioned }: {
  userId: number
  hasEndpoint: boolean
  onProvisioned: (username: string) => void
}) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => usersApi.provisionPlivoEndpoint(userId),
    onSuccess: (resp) => {
      if (resp.data?.username) onProvisioned(resp.data.username)
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  return (
    <div className="flex items-center gap-2 mt-1">
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => mutation.mutate()}
        loading={mutation.isPending}
        disabled={hasEndpoint}
      >
        <Phone size={13} />
        {hasEndpoint ? 'Endpoint Configured' : 'Auto-Provision Endpoint'}
      </Button>
      {mutation.isSuccess && (
        <span className="text-xs text-green-600">
          {mutation.data?.data?.already_exists ? 'Already exists' : 'Created!'}
        </span>
      )}
      {mutation.isError && (
        <span className="text-xs text-red-500">
          {(mutation.error as any)?.message || 'Failed — check Plivo settings'}
        </span>
      )}
    </div>
  )
}
